type FeatureItem = { icon?: string; title: string; description: string }

type FeatureGridData = {
  eyebrow?: string
  heading?: string
  subheading?: string
  columns?: number // 2 | 3 | 4, default 3
  items?: FeatureItem[]
}

const colsClass: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
}

export function FeatureGridSection({ data }: { data: FeatureGridData }) {
  const columns = data.columns ?? 3
  const gridCols = colsClass[columns] ?? colsClass[3]
  const items = data.items ?? []

  return (
    <section className="cms-section-contained bg-gradient-to-b from-[var(--background)] via-[var(--section-alt)]/40 to-[var(--background)] py-[var(--section-space)]">
      <div className="mx-auto w-full max-w-6xl min-w-0 px-4 md:px-6">
        {(data.eyebrow || data.heading || data.subheading) && (
          <header className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
            {data.eyebrow && (
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
                {data.eyebrow}
              </p>
            )}
            {data.heading && (
              <h2 className="text-balance text-[clamp(1.75rem,3.2vw,2.5rem)] font-extrabold tracking-tight text-[var(--primary)]">
                {data.heading}
              </h2>
            )}
            {data.subheading && (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-[var(--muted-foreground)] md:text-lg">
                {data.subheading}
              </p>
            )}
          </header>
        )}
        {items.length > 0 && (
          <div className={`grid gap-6 ${gridCols}`}>
            {items.map((item, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl border border-[var(--border)]/80 bg-[var(--card)] p-7 shadow-sm motion-safe:transition-all motion-safe:duration-200 motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-xl motion-safe:hover:border-[var(--accent)]/40 before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r before:from-[var(--accent)] before:to-[var(--primary)] before:opacity-0 motion-safe:before:transition-opacity motion-safe:group-hover:before:opacity-100"
              >
                {item.icon && (
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--primary)]/5 text-2xl text-[var(--primary)]" aria-hidden>
                    {item.icon}
                  </div>
                )}
                <h3 className="text-lg font-bold leading-snug tracking-tight text-[var(--primary)] md:text-xl">{item.title}</h3>
                <p className="mt-3 text-pretty text-sm leading-relaxed text-[var(--muted-foreground)] md:text-base">{item.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
